import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api } from '../../services/api'
import { Badge, Card, EmptyState, Spinner, StatTile, Table } from '../../components/ui'
import { clp, formatDateShort, APTITUDE_LABELS, ORDER_STATUS, QUOTE_STATUS } from '../../utils/format'
import type { PortalOrder, Quote } from '../../types/api'

interface Dashboard {
  counts: { ordersInProgress: number; ordersCompleted: number; pendingQuotes: number; openCharges: number }
  recentOrders: PortalOrder[]
  recentQuotes: Quote[]
  recentDocuments: Array<{ id: string; type: string; status: string; internalFolio: number; totalAmount: number; issuedAt?: string | null; createdAt: string; description: string }>
}

export function PortalDashboardPage() {
  const dashboard = useQuery({
    queryKey: ['portal-dashboard'],
    queryFn: async () => (await api.get<Dashboard>('/portal/dashboard')).data,
  })
  const company = useQuery({
    queryKey: ['portal-company'],
    queryFn: async () => (await api.get<{ businessName: string }>('/portal/company')).data,
  })

  if (dashboard.isLoading) return <Spinner />
  const d = dashboard.data

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-2xl font-bold text-ink">{company.data?.businessName ?? 'Mi empresa'}</h1>
        <p className="text-sm text-gray-500">Resumen de exámenes preocupacionales, cotizaciones y facturación.</p>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile label="Órdenes en curso" value={d?.counts.ordersInProgress ?? 0} accent="brand" />
        <StatTile label="Exámenes completados" value={d?.counts.ordersCompleted ?? 0} accent="success" />
        <StatTile label="Cotizaciones abiertas" value={d?.counts.pendingQuotes ?? 0} accent="ink" />
        <StatTile label="Cargos por pagar" value={d?.counts.openCharges ?? 0} accent={d?.counts.openCharges ? 'danger' : 'ink'} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card
          title="Últimas órdenes"
          actions={<Link to="/empresa/trabajadores" className="text-xs font-medium text-brand-700 hover:underline">Ver todas →</Link>}
        >
          {(d?.recentOrders.length ?? 0) === 0 ? (
            <EmptyState message="Aún no hay órdenes preocupacionales." />
          ) : (
            <Table headers={['Trabajador', 'Batería', 'Estado', 'Resultado']}>
              {d!.recentOrders.map((o) => (
                <tr key={o.id}>
                  <td className="px-2 py-2 font-medium">{o.worker.name}</td>
                  <td className="px-2 py-2 text-gray-600">{o.battery.name}</td>
                  <td className="px-2 py-2"><Badge className={ORDER_STATUS[o.status].className}>{ORDER_STATUS[o.status].label}</Badge></td>
                  <td className="px-2 py-2 text-sm">{APTITUDE_LABELS[o.result]}</td>
                </tr>
              ))}
            </Table>
          )}
        </Card>

        <Card
          title="Últimas cotizaciones"
          actions={<Link to="/empresa/cotizaciones" className="text-xs font-medium text-brand-700 hover:underline">Ver todas →</Link>}
        >
          {(d?.recentQuotes.length ?? 0) === 0 ? (
            <EmptyState message="Sin cotizaciones. Pide la primera desde la pestaña Cotizaciones." />
          ) : (
            <Table headers={['Código', 'Fecha', 'Estado', 'Total']}>
              {d!.recentQuotes.map((q) => (
                <tr key={q.id}>
                  <td className="px-2 py-2 font-mono text-xs">{q.codeLabel}</td>
                  <td className="px-2 py-2 text-gray-600">{formatDateShort(q.createdAt)}</td>
                  <td className="px-2 py-2"><Badge className={QUOTE_STATUS[q.status].className}>{QUOTE_STATUS[q.status].label}</Badge></td>
                  <td className="px-2 py-2">{q.total != null ? clp(q.total) : '—'}</td>
                </tr>
              ))}
            </Table>
          )}
        </Card>
      </div>

      <Card
        title="Últimos documentos"
        actions={<Link to="/empresa/facturacion" className="text-xs font-medium text-brand-700 hover:underline">Ver facturación →</Link>}
      >
        {(d?.recentDocuments.length ?? 0) === 0 ? (
          <EmptyState message="Sin documentos emitidos todavía." />
        ) : (
          <Table headers={['Folio', 'Detalle', 'Total', 'Estado']}>
            {d!.recentDocuments.map((doc) => (
              <tr key={doc.id}>
                <td className="px-2 py-2 font-mono text-xs">#{doc.internalFolio}</td>
                <td className="px-2 py-2 text-gray-600">{doc.description}</td>
                <td className="px-2 py-2">{clp(doc.totalAmount)}</td>
                <td className="px-2 py-2">
                  <Badge className={doc.status === 'ISSUED' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'}>
                    {doc.status === 'ISSUED' ? 'Emitido' : doc.status === 'DRAFT' ? 'Borrador' : 'Anulado'}
                  </Badge>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </div>
  )
}
